import React, { useState, useEffect, useRef, useMemo, useContext } from "react"
import { Content } from "./style"
import freight_act from '../../../assets/router/routersTabHeader/freight-active.svg'
import local_act from '../../../assets/sidemenu/local-active.svg'
import { ButtonCreate, LeftSide, OptionsButton, RightSide, SectionName, SubHeaderContainer, Tab, TabsDiv, FilterWrapper } from "../../../components/styles/subheader-style"
import { Outlet, useLocation, useNavigate, useOutletContext } from "react-router-dom"
import { TypeMainOutlet } from ".."
import RouterButtons from "./routerButtons"
import TableFiltersContext from "../../../contexts/TableFiltersContext"
import { usePersistedFilters } from '../../../hooks/usePersistedFilters'
import FilterMenu from '../../../components/SubHeaderFilter/FilterMenu'
import AppliedFiltersDisplay from '../../../components/SubHeaderFilter/AppliedFiltersDisplay'
import { Filter, Header } from '../../../components/SubHeaderFilter/types'
import { Filter as FilterIcon } from 'lucide-react'

export interface RouterOutletType {
    tabIndex: number
    setTabIndex: React.Dispatch<React.SetStateAction<number>>
    search: string
    appliedFilters: Filter[]
}

interface RouterBody {
    id: number
    date: string
    status: string
    freightIds: number[]
    track: string | undefined
    url: string
    origin: string
    vehicleId: number
}

const tabs = [
    { path: '', image: freight_act, name: 'Fretes' },
    { path: 'routers', image: local_act, name: 'Roteiros' }
]

export const freightHeaders: Header[] = [
    { key: 'status', label: 'Situação', type: 'string', selectOptions: [
        { value: 'Todos', label: 'Todos' },
        { value: 'A programar', label: 'A programar' },
        { value: 'Rota programada', label: 'Rota programada' },
        { value: 'Entregue', label: 'Entregue' },
        { value: 'Coletado', label: 'Coletado' },
        { value: 'Endereço pendente', label: 'Endereço pendente' },
        { value: 'Programado', label: 'Programado' },
        { value: 'Em trânsito', label: 'Em trânsito' },
        { value: 'Concluído', label: 'Concluído' }
    ]},
    { key: 'companyName', label: 'Empresa', type: 'string' },
    { key: 'address', label: 'Endereço', type: 'string' },
    { key: 'orderNumber', label: 'Pedido', type: 'string' },
    { key: 'observation', label: 'Observação', type: 'string' }
]

export const routeHeaders: Header[] = [
    { key: 'status', label: 'Situação', type: 'string', selectOptions: [
        { value: 'Todos', label: 'Todos' },
        { value: 'A programar', label: 'A programar' },
        { value: 'Rota programada', label: 'Rota programada' },
        { value: 'Rota concluida', label: 'Rota concluida' },
        { value: 'Programado', label: 'Programado' },
        { value: 'Em trânsito', label: 'Em trânsito' },
        { value: 'Concluído', label: 'Concluído' }
    ]},
    { key: 'vehiclePlate', label: 'Placa', type: 'string' },
    { key: 'origin', label: 'Origem', type: 'string' }
]

interface RouterSubHeaderProps {
    tabIndexActived: number
    appliedFilters: Filter[]
    onApplyFilter: (f: Filter) => void
    headers: Header[]
}

const RouterSubHeader: React.FC<RouterSubHeaderProps> = ({
    tabIndexActived,
    appliedFilters,
    onApplyFilter,
    headers
}) => {
    const navigate = useNavigate()
    const location = useLocation()
    const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false)
    const filterButtonRef = useRef<HTMLDivElement | null>(null)

    return (
        <SubHeaderContainer>
            <LeftSide>
                <SectionName>Roterização</SectionName>
                {(location.pathname !== '/panel/router/routers/create' && location.pathname !== '/panel/router/routers/edit') &&
                    <TabsDiv>
                        {tabs.map((tab, index) => (
                            <Tab
                                key={index}
                                $isFirst={index === 0}
                                $isLast={index === tabs.length - 1}
                                $isActive={index === tabIndexActived}
                                onClick={() => {
                                    if (index !== tabIndexActived) navigate(tab.path)
                                }}
                            >
                                <img src={tab.image} alt={`${tab.path}-icon`} />
                                {tab.name}
                            </Tab>
                        ))}
                    </TabsDiv>}
            </LeftSide>
            <RightSide>
                {(location.pathname !== '/panel/router/routers/create' && location.pathname !== '/panel/router/routers/edit') &&
                    <>
                        <FilterWrapper>
                            <OptionsButton ref={filterButtonRef} onClick={() => setIsFilterMenuOpen(true)}>
                                <FilterIcon size={18} />
                            </OptionsButton>
                            {isFilterMenuOpen && (
                                <FilterMenu
                                    headers={headers}
                                    onApplyFilter={(f) => {
                                        onApplyFilter(f)
                                        setIsFilterMenuOpen(false)
                                    }}
                                    onClose={() => setIsFilterMenuOpen(false)}
                                    anchorRef={filterButtonRef}
                                    filters={appliedFilters}
                                />
                            )}
                        </FilterWrapper>
                        <RouterButtons index={tabIndexActived} />
                    </>
                }
            </RightSide>
        </SubHeaderContainer>
    )
}

const RouterContent = () => {
    const [tabIndex, setTabIndex] = useState<number>(0)
    const { search } = useOutletContext<TypeMainOutlet>()
    const location = useLocation()
    const { setFreightStatusFilter, setRouterStatusFilter } = useContext(TableFiltersContext)

    const freightFiltersState = usePersistedFilters('freight')
    const routeFiltersState = usePersistedFilters('route')

    const activeFiltersState = tabIndex === 0 ? freightFiltersState : routeFiltersState
    const activeHeaders = tabIndex === 0 ? freightHeaders : routeHeaders

    useEffect(() => {
        if (location.pathname.includes('/router/routers')) {
            setTabIndex(1)
        } else {
            setTabIndex(0)
        }
    }, [location.pathname])

    useEffect(() => {
        const statusFilter = freightFiltersState.appliedFilters.find(f => f.key === 'status')
        setFreightStatusFilter(statusFilter ? String(statusFilter.value) : 'Todos')
    }, [freightFiltersState.appliedFilters, setFreightStatusFilter])

    useEffect(() => {
        const statusFilter = routeFiltersState.appliedFilters.find(f => f.key === 'status')
        setRouterStatusFilter(statusFilter ? String(statusFilter.value) : 'Todos')
    }, [routeFiltersState.appliedFilters, setRouterStatusFilter])

    const isCreateOrEdit = location.pathname === '/panel/router/routers/create' || location.pathname === '/panel/router/routers/edit'

    return (
        <>
            <RouterSubHeader
                tabIndexActived={tabIndex}
                appliedFilters={activeFiltersState.appliedFilters}
                onApplyFilter={activeFiltersState.handleApplyFilter}
                headers={activeHeaders}
            />

            {!isCreateOrEdit && activeFiltersState.appliedFilters.length > 0 && (
                <AppliedFiltersDisplay
                    filters={activeFiltersState.appliedFilters}
                    onRemoveFilter={activeFiltersState.handleRemoveFilter}
                    onClearAll={activeFiltersState.handleClearAll}
                />
            )}

            <Content>
                <Outlet
                    context={{
                        tabIndex,
                        setTabIndex,
                        search,
                        appliedFilters: activeFiltersState.appliedFilters
                    }}
                />
            </Content>
        </>
    )
}

export default RouterContent